import BaseContextMenu from "@components/ContextMenu/BaseContextMenu";
import './ColorPickerContextMenu.scss';
import { useMemo } from "react";
import { usePanelContainer } from "@features/analysis/rendering/Panels/usePanelContainer";
import CustomSlider from "@components/CustomSlider/CustomSlider";
import { initialcolorandopacity, updateLabelmapSegment } from "@features/analysis/rendering/Panels/Renderers/CornerstoneManager";
import { SegmentationControllerType, SegmentationProps } from "config/analysisConfig";
import { useAnalysisPage } from "@features/analysis/useAnalysisPage";
import { scheduleRender } from "@features/analysis/rendering/Panels/renderState";

type Color = [number,number,number]
interface ColorPickerContextMenuProps {
  Colors : Color[];
  selectedColor?: Color;
  selectedKey?: SegmentationControllerType;
  onPick?: (color: Color) => void;
  onClose: () => void;
}

const ColorPickerContextMenu = ({
  Colors = [],
  selectedColor,
  selectedKey,
  onPick,
  onClose
}: ColorPickerContextMenuProps) => {
  const {selectedSeriesId} = useAnalysisPage()
  const {updatePanelState, panelStates, analysisType} = usePanelContainer();
  const {segmentation} = panelStates['Surface3D']
  if(!segmentation || !selectedKey) return(<></>);
  const PALETTE_COLORS : Color[]= [
    // "#FF0000", "#FF8000", "#FFFF00", "#80FF00", "#00FF00", "#00FF80",
    // "#00FFFF", "#0080FF", "#0000FF", "#8000FF", "#FF00FF", "#FF0080"
    [255,   0,   0], // #FF0000
    [255, 128,   0], // #FF8000
    [255, 255,   0], // #FFFF00
    [128, 255,   0], // #80FF00
    [  0, 255,   0], // #00FF00
    [  0, 255, 128], // #00FF80
    [  0, 255, 255], // #00FFFF
    [  0, 128, 255], // #0080FF
    [  0,   0, 255], // #0000FF
    [128,   0, 255], // #8000FF
    [255,   0, 255], // #FF00FF
    [255,   0, 128], // #FF0080
    [ 97, 187, 70],  // #61bb46 aorta
    [245, 130, 31],  // #F5821F la
    [224,  58, 62],  // #E03A3E lv
    [150,  61, 151], // #963D97 ra
    [  0, 157, 220], // #009DDC rv
    [184,  97, 24],  // #B86118 laWall
    [161,  42, 45],  // #A12A2D lvWall
    [ 92,  37, 92],  // #5C255C raWall
    [  0,  91, 136], // #005B88 rvWall
    [255, 204, 204], // #FFCCCC coronary
    [255, 255, 255], // #FFFFFF calcium
    [227, 225, 149], // #E3E195 epicardial
    [161,  42, 45],  // #A12A2D avc
  ];
  const mergedColors = useMemo(()=>{
    const set = new Set(
        [...PALETTE_COLORS].map(rgb => rgb.join(','))
      );
      return Array.from(set).map(str => str.split(',').map(Number) as Color);
  },[])

  const handleSegmentation = (
    key : keyof SegmentationProps,
    value : SegmentationProps[keyof SegmentationProps],
    target?:SegmentationControllerType
  )=>{
    const targetKey = target??selectedKey;
    if(!selectedSeriesId) return;
    let {color,opacity,visibility} = segmentation[targetKey];
    switch (key) {
      case 'opacity':
        opacity = value as number;
        visibility = opacity!==0;
        break;
      case 'visibility':
        const vis = value as boolean;
        opacity = vis?1:0;
        visibility = vis;
        break;
      case 'color':
        color = value as [number,number,number];
        break;
    }
    const newSegmentation = {
      ...segmentation,
      [targetKey]:{
        color,
        opacity,
        visibility,
      }
    };
    updateLabelmapSegment({
      // seriesId:selectedSeriesId,
      segmentTarget:targetKey,
      segmentations:newSegmentation
    })

    updatePanelState(targetKey,'segmentation',{
      ...newSegmentation
    })

    initialcolorandopacity(analysisType, target??selectedKey, newSegmentation);
    scheduleRender('Surface3D', { type: 'updateSegmentation', name: target??selectedKey, newSegmentation});
    if(key === 'visibility')
      scheduleRender('VR', { type: 'updateSegmentation', name: target??selectedKey, newSegmentation});
  }

  const handleOpacityChange = (newValue: number) => {
    if(newValue!==segmentation[selectedKey]?.opacity)
    handleSegmentation('opacity',newValue)
  };

  return(
    <BaseContextMenu onClose = {onClose}>
      <div className="color-palette-container">
        <div className="color-palette-top">
          {mergedColors.map((color) => (
            <div
              key={`${color}`}
              className={`color-chip${selectedColor === color ? " selected" : ""}`}
              style={{ backgroundColor: `rgb(${color[0]},${color[1]},${color[2]})` }}
              onClick={() => onPick?.(color)}
              // title={`${color}`}
            />
          ))}
        </div>
        <div className="color-palette-bottom">
          <div className="slider-segment">
            <CustomSlider
              type='default'
              value={segmentation[selectedKey].opacity}
              onChange={handleOpacityChange}
              min={0}
              max={1}
              step={0.1}
            />
          </div>
          <div className="segment-value-rename">
            {`${segmentation[selectedKey].opacity*100}%`}
          </div>
        </div>
      </div>
    </BaseContextMenu>
  )
}

export default ColorPickerContextMenu;
